import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Navbar } from 'react-bootstrap';
import {
  LayoutDashboard,
  CircleHelp,
  Sparkles,
  CalendarDays,
  Camera,
  ClipboardList,
  Share2,
  MoreHorizontal,
  LucideIcon
} from 'lucide-react';
import './trip-open-view-navbar.scss';

interface TripOpenViewNavbarProps {
  tripTitle: string;
  activeSection?: string;
  onShareClick?: () => void;
  onMoreClick?: () => void;
}

const SECTIONS = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'information', label: 'Info', icon: CircleHelp },
  { key: 'discovery', label: 'Discovery', icon: Sparkles },
  { key: 'itineraries', label: 'Itinerary', icon: CalendarDays },
  { key: 'photos', label: 'Photos', icon: Camera },
  { key: 'recap', label: 'Recap', icon: ClipboardList }
].map((section) => section as { key: string; label: string; icon: LucideIcon });

const TripOpenViewNavbar: React.FC<TripOpenViewNavbarProps> = ({
  tripTitle,
  activeSection = 'overview',
  onShareClick,
  onMoreClick
}) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const handleNavigate = (section: string) => {
    navigate(`/app/trip/${id}/view/${section}`);
  };

  const handleBackClick = () => {
    navigate('/app/trips');
  };

  return (
    <Navbar
      className='trip-open-view-navbar'
      role='navigation'
      aria-label='Trip view navigation'
    >
      <div className='navbar-content'>
        <div className='trip-title-section'>
          <div className='trip-header-row'>
            <h2
              className='trip-title font-libre'
              id='trip-title'
            >
              <button
                type='button'
                className='trip-back-link'
                onClick={handleBackClick}
                aria-label='Back to My Trips'
              >
                My Trips
              </button>
              <span className='title-separator'> &nbsp;&gt;&nbsp; </span>
              <div className='trip-name'>{tripTitle}</div>
            </h2>

            <div className='trip-actions'>
              <button
                type='button'
                className='trip-action-btn'
                onClick={onShareClick}
                aria-label='Share trip'
              >
                <Share2
                  size={13}
                  aria-hidden='true'
                />
                <span>Share</span>
              </button>
              <button
                type='button'
                className='trip-action-btn'
                onClick={onMoreClick}
                aria-label='More trip actions'
              >
                <MoreHorizontal
                  size={13}
                  aria-hidden='true'
                />
                <span>More</span>
              </button>
            </div>
          </div>

          <div
            className='view-toolbar'
            role='tablist'
            aria-label='Trip sections'
          >
            {SECTIONS.map((section) => {
              const isActive = section.key === activeSection;
              const Icon = section.icon;
              return (
                <button
                  key={section.key}
                  type='button'
                  role='tab'
                  aria-selected={isActive}
                  aria-current={isActive ? 'page' : undefined}
                  className={`view-toolbar-button ${isActive ? 'active' : ''}`}
                  onClick={() => handleNavigate(section.key)}
                >
                  <Icon
                    size={12}
                    strokeWidth={2.1}
                    className='toolbar-icon'
                    aria-hidden='true'
                  />
                  <span className='toolbar-label'>{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </Navbar>
  );
};

export default TripOpenViewNavbar;
